// @givmo/board — a batch of past records the organization's connected AI proposed, and the ONE act
// that puts them in the minute book (CBG-AI5-PROPOSE, frames b1–b8; backend `board-ai-proposals.md`
// §3.3, §6.2).
//
// ══ WHAT THIS PAGE HAS TO MAKE HONEST ══════════════════════════════════════════════════════════════
//
//   1. NOTHING HERE IS THE RECORD YET (plan §3.3). The page says so in its first line, and the only
//      way in is the one confirmation, a person's act (D-CBG-34 (1); the backend refuses any other
//      origin, design §6.3). Confirming enters every record through RC8's one import door, all or
//      nothing; afterwards the import page takes over (CBG-UX11: no screen names a batch, and the
//      batch Board keeps for the confirmation is internal, its name and account Board's own).
//   2. A FILE IS THERE OR IT ISN'T, AT A GLANCE (D-CBG-34 (3)). However a file came to be missing
//      (the client couldn't send it, over 50 MB, the wrong kind), the row reads one way: "No file
//      yet", the one coloured word on the page, its reason in a quiet line, and "Add the file" beside
//      it. The line just above the two acts counts them and says why it matters.
//   3. WHAT GOES IN IS SEEN FIRST (givmo-design: context precedes commitment). The facts sit at the
//      top; the records follow; the two acts come only after the last record, at every width. No
//      sticky footer. Each title opens the record as it would go in, word for word, with its file.
//   4. A MISTAKE IS LEFT OUT, NOT EDITED. Board never rewrites what the AI proposed.
//   5. THE AI'S TEXT IS INERT. Titles, notes, text and file names render as plain text, the record's
//      text in `whitespace-pre-wrap` (design §6, N4): a staged URL never becomes a link.
//   6. A RECORD THE BOOK ALREADY HOLDS SAYS SO, QUIETLY (CBG-UX11, D-CBG-39 (6); frames c1–c3): its
//      row names the book's record by its own title, linked, and the confirmation names it before
//      the act. Never a refusal: the person leaves it out or imports it anyway. The backend decides
//      what counts as a match, among the records this person may read (B10).
//   7. A CLOSED SESSION'S RECORD IS MARKED AS THE MINUTE BOOK MARKS ONE (CBG-AI5-CLOSED, D-CBG-44):
//      its row and its read say so, its menu changes the mark, and who will read it is said as the
//      mark is chosen and again in the confirmation. It goes in as one entered by hand does.
//
// As a person opens the page, it asks Board to check each file still awaited (design §3.3, way 2:
// one request per file); the acts wait for those answers, since each moves the batch's revision and
// the confirmation echoes the revision the person saw.

import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Icon,
  RowActions,
  Skeleton,
  toast,
} from '@givmo/design-system';
import { FileText, Lock } from 'lucide-react';
import { useBoardHost } from '../../board-host';
import { BOARD_PAGES } from '../../board-routes';
import { Link, useNavigate, useParams } from '../../board-routing';
import { useBoardOperations } from '../../board-runtime';
import { FieldNote } from '../../components/field-note';
import { StatusWord } from '../../components/fine-print';
import { PageShell } from '../../components/page-shell';
import { displayDate } from '../../lib/display';
import { isNotServed } from '../../lib/not-served';
import { DialogFrame, PartLabel } from '../conflicts/coi-parts';
import { recordKindLabel } from '../records/records-decode';
import { fileSize } from '../returns/return-decode';
import { servedSentence } from '../returns/return-queries';
import {
  FILE_ACCEPT,
  MAX_FILE_BYTES,
  chosenFileType,
  fileCheckRefusal,
  goingInWithoutALink,
  hasFile,
  isClosedSession,
  missingReason,
  namesAFile,
  othersWithTheSameFile,
  putToSlot,
  recordLinks,
  recordTitle,
  recordsNamingThis,
  stagedRecords,
  versionsReplacing,
  type LinkKind,
  type PastRecordBatch,
  type PastRecordProposal,
} from './proposal-decode';
import { PAST_RECORDS_PERMISSION, useProposedBatch, useSettleProposals } from './proposal-queries';
import { AiRationale } from './ai-rationale';
import { isWordFile, proposalWords as w } from './proposal-words';
import { closedProposalWords as cw } from './closed-proposal-words';
import { uploadWords as uw } from '../import/upload-words';

const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const BACK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
/** The package's own fallbacks, for a refusal that carries no sentence of its own. */
const COULD_NOT = 'We couldn’t do that. Please try again.';
const COULD_NOT_OPEN = 'We couldn’t open that file. Please try again.';

/** A refusal this page words itself (a file's kind or size, a PUT storage refused): shown as it is. */
class SaidError extends Error {}

/** "Minutes · Mar 14, 2024": what the record is, and the date it is kept from. */
function kindAndDate(record: PastRecordProposal): string {
  const kind = recordKindLabel(record.record_kind);
  return record.occurred_on ? `${kind} · ${displayDate(record.occurred_on)}` : kind;
}

/** Open a signed read in a new tab: the file downloads under its own name, never inline. */
function useOpenStagedFile(record: PastRecordProposal) {
  const operations = useBoardOperations();
  const { orgRef } = useBoardHost();
  return useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerGetBoardProposalFileUrl({
        path: { org_ref: orgRef ?? '', proposal_id: record.id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (link) => {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    onError: (caught) => toast.error(servedSentence(caught, COULD_NOT_OPEN)),
  });
}

/**
 * Way 2 of Board's check (design §3.3): each file still awaited when the page opens is asked about
 * once, one request per file. Answers whether those checks are still out.
 */
function useArrivalChecks(batch: PastRecordBatch | null): boolean {
  const operations = useBoardOperations();
  const { orgRef } = useBoardHost();
  const settle = useSettleProposals();
  const asked = useRef(new Set<string>());
  const { mutate: check, isPending } = useMutation({
    mutationFn: async (ids: readonly string[]) =>
      Promise.allSettled(
        ids.map((id) =>
          operations.officerCheckBoardProposalFile({
            path: { org_ref: orgRef ?? '', proposal_id: id },
            body: {},
            throwOnError: true,
          }),
        ),
      ),
    onSuccess: async (answers) => {
      const moved = answers.some(
        (answer) =>
          answer.status === 'fulfilled' &&
          answer.value.data.proposal.file.state !== 'awaiting_upload',
      );
      if (moved) await settle();
    },
  });
  const awaited =
    batch === null || orgRef === null
      ? ''
      : stagedRecords(batch)
          .filter((record) => record.file.state === 'awaiting_upload')
          .map((record) => record.id)
          .join(',');
  useEffect(() => {
    const fresh = awaited.split(',').filter((id) => id.length > 0 && !asked.current.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) asked.current.add(id);
    check(fresh);
  }, [awaited, check]);
  return isPending;
}

export function ProposedBatchPage() {
  const { batchId } = useParams({ from: '/authenticated/import-batches/proposed/$batchId' });
  const { isReady, permissions } = useBoardHost();
  const canImport = permissions?.includes(PAST_RECORDS_PERMISSION) === true;
  const navigate = useNavigate();
  const batchQuery = useProposedBatch(batchId);
  const batch = batchQuery.data ?? null;
  const checking = useArrivalChecks(batch);
  const [reading, setReading] = useState<PastRecordProposal | null>(null);
  const [adding, setAdding] = useState<PastRecordProposal | null>(null);
  const [leaving, setLeaving] = useState<PastRecordProposal | null>(null);
  const [marking, setMarking] = useState<PastRecordProposal | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  // Ended, imported or discarded, these records are no longer this page's: the import page, the
  // address it sits beneath, takes over (CBG-UX11; the records imported are listed there).
  const ended = batch !== null && batch.state !== 'staged';
  useEffect(() => {
    if (ended) void navigate({ to: BOARD_PAGES.importBatches, replace: true });
  }, [ended, navigate]);

  const back = (
    <Link to={BOARD_PAGES.importBatches} exact className={BACK} data-testid="proposed-batch-back">
      {w.backToImport}
    </Link>
  );

  if (!isReady || (canImport && batchQuery.isPending) || ended) {
    return (
      <PageShell title={w.recordsToConfirm} testId="page-proposed-batch">
        <Skeleton className="h-90 w-full rounded-lg" testId="proposed-batch-loading" />
      </PageShell>
    );
  }

  if (!canImport || batchQuery.isError || batch === null) {
    // A reader who can't import is asked nothing (the read is behind the confirmation's string), and
    // reads what records no one can open read.
    return (
      <PageShell title={w.recordsToConfirm} actions={back} testId="page-proposed-batch">
        <Card testId="proposed-batch-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {w.couldNotOpenRecords}
            </p>
            {canImport && !isNotServed(batchQuery.error) ? (
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => void batchQuery.refetch()}
                testId="proposed-batch-retry"
              >
                Try again
              </Button>
            ) : null}
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  const records = stagedRecords(batch);
  const attached = records.filter(hasFile).length;
  const missing = records.length - attached;
  const busy = checking || batchQuery.isFetching;

  return (
    <PageShell
      title={w.batchTitle}
      description={w.batchLead}
      actions={back}
      testId="page-proposed-batch"
    >
      {/* One lead, read as one block: the set's facts and how to read a record. */}
      <div className="flex flex-col gap-4">
        <p className={QUIET} data-testid="proposed-batch-facts">
          {w.batchFacts(records.length, attached, displayDate(batch.created_at))}
        </p>
        {records.length > 0 ? (
          <p className={QUIET} data-testid="proposed-batch-read-first">
            {w.readBeforeConfirming}
          </p>
        ) : null}
      </div>

      {records.length > 0 ? (
        <Card testId="proposed-batch-records">
          <CardBody className="flex flex-col">
            <ul className="flex flex-col">
              {records.map((record) => (
                <RecordRow
                  key={record.id}
                  record={record}
                  batch={batch}
                  onRead={() => setReading(record)}
                  onAddFile={() => setAdding(record)}
                  onLeaveOut={() => setLeaving(record)}
                  onMark={() => setMarking(record)}
                />
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {/* After the records, never before them: what's missing, then the batch's two acts. */}
      <div className="flex flex-col gap-14" data-testid="proposed-batch-acts">
        {missing > 0 ? (
          <p className={LINE} data-testid="proposed-batch-missing">
            {w.missingCallout(missing, records.length)}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-10">
          {records.length > 0 ? (
            <Button
              appearance="saas"
              tone="primary"
              disabled={busy}
              onClick={() => setConfirming(true)}
              testId="proposed-batch-confirm"
            >
              {w.confirmButton(records.length)}
            </Button>
          ) : null}
          <Button
            appearance="saas"
            tone="secondary"
            disabled={busy}
            onClick={() => setDiscarding(true)}
            testId="proposed-batch-discard"
          >
            {w.discardButton(records.length)}
          </Button>
        </div>
      </div>

      {reading !== null ? (
        <ReadRecordDialog record={reading} batch={batch} onClose={() => setReading(null)} />
      ) : null}
      {adding !== null ? <AddFileDialog record={adding} onClose={() => setAdding(null)} /> : null}
      {leaving !== null ? (
        <LeaveOutDialog
          record={leaving}
          batch={batch}
          revision={batch.revision}
          onClose={() => setLeaving(null)}
        />
      ) : null}
      {marking !== null ? (
        <MarkClosedDialog
          record={marking}
          revision={batch.revision}
          onClose={() => setMarking(null)}
        />
      ) : null}
      {confirming ? (
        <ConfirmBatchDialog batch={batch} onClose={() => setConfirming(false)} />
      ) : null}
      {discarding ? (
        <DiscardBatchDialog
          batch={batch}
          records={records.length}
          attached={attached}
          fromUploads={records.filter((record) => record.file.inbox_file_id).length}
          onClose={() => setDiscarding(false)}
        />
      ) : null}
    </PageShell>
  );
}

/**
 * A staged file, there: its name and size, with a document icon (copy 3.6: no word). On its row the
 * name is the way to read the record (CBG-UX13, Jonathan 2026-10-29 11:43 PDT: "the filename should
 * be a blue link you can click to read it"): the design system's button in its link tone, opening
 * what the ⋯ menu's "Read it" opened, its long name still truncated, and whole on hover (`title`)
 * and for a screen reader (its text is the whole name). In the record's read it is plain, beside
 * "Open the file".
 *
 * ⚠ The button wraps its children in a span of its own, and that span is the flex item that must
 * truncate: a `truncate` span nested inside it is inline, where truncation does nothing, so a long
 * name ran through the size and the ⋯ menu (review-318's F1). The button's `[&>span]:` classes
 * truncate that span itself.
 */
function FileThere({
  record,
  className,
  onRead,
}: {
  record: PastRecordProposal;
  className: string;
  onRead?: () => void;
}) {
  const name = record.file.file_name ?? '';
  return (
    <span className="flex min-w-0 max-w-full items-center gap-8">
      <Icon icon={FileText} size={16} aria-hidden className="shrink-0 text-icon-muted" />
      {onRead !== undefined ? (
        <Button
          appearance="saas"
          tone="link"
          size="sm"
          title={name}
          onClick={onRead}
          className="min-w-0 max-w-full justify-start [&>span]:min-w-0 [&>span]:truncate"
          testId={`proposed-record-file-open-${record.id}`}
        >
          {name}
        </Button>
      ) : (
        <span
          className={`truncate font-sans font-w500 tracking-givmo text-text-secondary ${className}`}
        >
          {name}
        </span>
      )}
      {typeof record.file.byte_size === 'number' ? (
        <span className="shrink-0 font-sans text-s12 tracking-givmo text-text-tertiary">
          {fileSize(record.file.byte_size)}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The quiet flag (frames c1, c2): the minute book already holds this record, named by the book's own
 * title and linked to it. Nothing for a record the book doesn't hold.
 */
function AlreadyInTheBook({ record }: { record: PastRecordProposal }) {
  const held = record.already_recorded_as ?? null;
  if (held === null) return null;
  return (
    <span
      className="mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary wrap-anywhere"
      data-testid={`proposed-record-in-the-book-${record.id}`}
    >
      {w.alreadyInTheBook}
      <Link
        to="/records/$recordId"
        params={{ recordId: held.record_id }}
        className="font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
        data-testid={`proposed-record-in-the-book-link-${record.id}`}
      >
        {held.title}
      </Link>
      {w.alreadyInTheBookAfter}
    </span>
  );
}

/** A closed session's record, marked as the minute book marks one (CBG-AI5-CLOSED). */
function ClosedSessionMark({ record, testId }: { record: PastRecordProposal; testId: string }) {
  if (!isClosedSession(record)) return null;
  return (
    <span
      className="inline-flex items-center gap-4 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
      data-testid={testId}
    >
      <Icon icon={Lock} size={12} aria-hidden />
      {cw.closedSession}
    </span>
  );
}

const LINK_LABEL: Record<LinkKind, string> = {
  adopted_by: w.adoptedBy,
  meeting: w.aboutTheMeeting,
  supersedes: w.replaces,
};

/**
 * CBG-FIX160 L (D-CBG-47, frame l1): a record's links, each its label, then the linked record's
 * title and where it is ("proposed above", "in your minute book"), so every link the record goes
 * in with is on the page before the confirmation. A link to a record the person left out says the
 * record goes in without it (design §8.3), as the confirmation does: it drops the link.
 */
function RecordLinks({
  record,
  batch,
  testId,
}: {
  record: PastRecordProposal;
  batch: PastRecordBatch;
  testId: string;
}) {
  const links = recordLinks(record, batch);
  if (links.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-2" data-testid={testId}>
      {links.map((link) => (
        <p
          key={link.kind}
          className={`${LINE} wrap-anywhere`}
          data-testid={`${testId}-${link.kind}`}
        >
          <span className="font-w600 text-text-strong">{LINK_LABEL[link.kind]}</span>{' '}
          {link.where === 'left_out' ? (
            link.kind === 'meeting' ? (
              w.meetingLeftOut
            ) : (
              w.adoptedByLeftOut
            )
          ) : link.title === null ? (
            w.aRecordInTheBook
          ) : (
            <>
              {link.title}
              <span className="text-text-tertiary">
                {link.where === 'proposed' ? w.proposedAbove : w.inYourMinuteBook}
              </span>
            </>
          )}
        </p>
      ))}
    </div>
  );
}

/**
 * CBG-FIX160 G (D-CBG-47, frame g1): which pages of a signed file holding several records are this
 * record's, and that the same file goes with other records here. The file stays whole with each.
 */
function FilePages({ record, batch }: { record: PastRecordProposal; batch: PastRecordBatch }) {
  const pages = record.file.pages ?? null;
  const others = othersWithTheSameFile(record, batch);
  return (
    <>
      {pages !== null ? (
        <span className={LINE} data-testid={`proposed-record-pages-${record.id}`}>
          {w.filePages(pages.first, pages.last, record.file.page_count ?? null)}
        </span>
      ) : null}
      {others > 0 ? (
        <span className={QUIET} data-testid={`proposed-record-same-file-${record.id}`}>
          {w.sameFileWith(others)}
        </span>
      ) : null}
    </>
  );
}

/** One proposed record: what it is, where it came from, and its file there or not. */
function RecordRow({
  record,
  batch,
  onRead,
  onAddFile,
  onLeaveOut,
  onMark,
}: {
  record: PastRecordProposal;
  batch: PastRecordBatch;
  onRead: () => void;
  onAddFile: () => void;
  onLeaveOut: () => void;
  onMark: () => void;
}) {
  const present = hasFile(record);
  const reason = missingReason(record);
  const id = record.id;
  const title = recordTitle(record);
  // The row's title describes its rationale toggle (CBG-FIX170): every row's reads the same.
  const titleId = useId();
  return (
    <li
      className="relative grid grid-cols-1 gap-10 border-t border-border-subtle py-14 first:border-t-0 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,300px)_32px] sm:gap-16"
      data-testid={`proposed-record-${id}`}
    >
      <div className="flex min-w-0 flex-col gap-2 max-sm:pr-36">
        <button
          type="button"
          id={titleId}
          onClick={onRead}
          className="text-left font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong outline-none transition-colors wrap-anywhere hover:text-brand-700 focus-visible:underline"
          data-testid={`proposed-record-read-${id}`}
        >
          {title}
        </button>
        <span className={QUIET}>{kindAndDate(record)}</span>
        <ClosedSessionMark record={record} testId={`proposed-record-closed-${id}`} />
        <RecordLinks record={record} batch={batch} testId={`proposed-record-links-${id}`} />
        {(record.source_note ?? '').length > 0 ? (
          <AiRationale
            note={record.source_note ?? ''}
            testId={`proposed-record-note-${id}`}
            describedBy={titleId}
          />
        ) : null}
        <AlreadyInTheBook record={record} />
      </div>

      <div
        className="flex min-w-0 flex-col items-start gap-6"
        data-testid={`proposed-record-file-${id}`}
      >
        {present ? (
          <>
            <FileThere record={record} className="text-s13" onRead={onRead} />
            <FilePages record={record} batch={batch} />
            {/* CBG-AI5-INBOX-WEB (frame c4): a file a person uploaded for the AI to read. */}
            {record.file.inbox_file_id ? (
              <span className={QUIET} data-testid={`proposed-record-from-uploads-${id}`}>
                {uw.fromYourUploads}
              </span>
            ) : null}
          </>
        ) : (
          <>
            <StatusWord tone="act" testId={`proposed-record-missing-${id}`}>
              {w.noFileYet}
            </StatusWord>
            {reason !== null ? <span className={`${QUIET} wrap-anywhere`}>{reason}</span> : null}
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={onAddFile}
              testId={`proposed-record-add-file-${id}`}
            >
              {w.addTheFile}
            </Button>
          </>
        )}
      </div>

      <div className="max-sm:absolute max-sm:right-0 max-sm:top-10 first:max-sm:top-0">
        <RowActions
          testId={`proposed-record-actions-${id}`}
          label={`Actions for ${title}`}
          actions={[
            {
              label: present ? w.replaceTheFile : w.addTheFile,
              testId: `proposed-record-menu-file-${id}`,
              onSelect: onAddFile,
            },
            {
              label: isClosedSession(record) ? cw.markNotClosed : cw.markClosed,
              testId: `proposed-record-menu-closed-${id}`,
              onSelect: onMark,
            },
            {
              label: w.leaveOut,
              testId: `proposed-record-menu-leave-${id}`,
              destructive: true,
              onSelect: onLeaveOut,
            },
          ]}
        />
      </div>
    </li>
  );
}

/** The record as it would go in, word for word, with its file (frame b4). */
function ReadRecordDialog({
  record,
  batch,
  onClose,
}: {
  record: PastRecordProposal;
  batch: PastRecordBatch;
  onClose: () => void;
}) {
  const reason = missingReason(record);
  const open = useOpenStagedFile(record);
  const body = record.body ?? '';
  // Focus starts on Close, never on the file's link: that sits below the text, and focusing it
  // would scroll the record away from its top as it opens (at phone width, 186px into the text).
  const closeRef = useRef<HTMLButtonElement | null>(null);
  return (
    <DialogFrame
      title={recordTitle(record)}
      lead={w.wordForWord}
      testId="proposed-record-read-dialog"
      width={600}
      onClose={onClose}
      initialFocus={closeRef}
      footer={
        <Button
          ref={closeRef}
          appearance="saas"
          tone="secondary"
          onClick={onClose}
          testId="proposed-record-read-close"
        >
          {w.close}
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        <p className={QUIET}>{kindAndDate(record)}</p>
        <ClosedSessionMark record={record} testId="proposed-record-read-closed" />
        <RecordLinks record={record} batch={batch} testId="proposed-record-read-links" />
      </div>
      {/* Optional on every record (CBG-UX11, backend B9): no part for a note not given. */}
      {(record.source_note ?? '').length > 0 ? (
        <section className="flex flex-col gap-6">
          <PartLabel>{w.whereFrom}</PartLabel>
          <p className={`${LINE} whitespace-pre-wrap wrap-anywhere`}>{record.source_note}</p>
        </section>
      ) : null}
      <section className="flex flex-col gap-6">
        <PartLabel>{w.whatItSays}</PartLabel>
        {body.trim().length > 0 ? (
          <p
            className={`${LINE} whitespace-pre-wrap wrap-anywhere text-text-strong`}
            data-testid="proposed-record-read-body"
          >
            {body}
          </p>
        ) : (
          <p className={LINE}>{w.noText(namesAFile(record))}</p>
        )}
      </section>
      <section className="flex flex-col gap-6">
        <PartLabel>{w.theFile}</PartLabel>
        {hasFile(record) ? (
          <div className="flex flex-col gap-4">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-12 gap-y-4">
              <FileThere record={record} className="text-s14" />
              <button
                type="button"
                disabled={open.isPending}
                onClick={() => open.mutate()}
                className="shrink-0 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:text-text-tertiary"
                data-testid="proposed-record-open-file"
              >
                {isWordFile(record.file.content_type) ? w.downloadTheFile : w.openTheFile}
              </button>
            </div>
            <FilePages record={record} batch={batch} />
          </div>
        ) : (
          <div className="flex flex-col items-start gap-4">
            <StatusWord tone="act">{w.noFileYet}</StatusWord>
            {reason !== null ? <span className={`${QUIET} wrap-anywhere`}>{reason}</span> : null}
          </div>
        )}
      </section>
    </DialogFrame>
  );
}

/**
 * A missing file dropped in by the person (frame b3), or a file replaced: Board's own slot for this
 * one record, the browser's PUT, then Board's check (design §3.3, way 3). The page refuses a file
 * over 50 MB, or of a kind Board doesn't take, before anything uploads; Board's check reads what
 * arrived and turns down the wrong kind, too large or unreadable, and the dialog stays open.
 */
function AddFileDialog({ record, onClose }: { record: PastRecordProposal; onClose: () => void }) {
  const operations = useBoardOperations();
  const { orgRef } = useBoardHost();
  const settle = useSettleProposals();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [stage, setStage] = useState<'upload' | 'check'>('upload');
  const [error, setError] = useState<string | null>(null);
  const named = (record.file.file_name ?? '').trim();

  const add = useMutation({
    mutationFn: async (file: File) => {
      const contentType = chosenFileType(file);
      if (contentType === null) throw new SaidError(w.addFileKinds);
      // Before anything uploads (design §3.3): a person's slot carries no size range.
      if (file.size > MAX_FILE_BYTES) throw new SaidError(w.chosenTooLarge);
      const path = { org_ref: orgRef ?? '', proposal_id: record.id };
      setStage('upload');
      const issued = await operations.officerIssueBoardProposalFileUploadUrl({
        path,
        body: { file_name: file.name, content_type: contentType },
        throwOnError: true,
      });
      if (file.size > issued.data.max_byte_size) throw new SaidError(w.chosenTooLarge);
      if (!(await putToSlot(issued.data, file))) throw new SaidError(w.fileDidNotArrive);
      setStage('check');
      await operations.officerCheckBoardProposalFile({
        path,
        body: {
          object_ref: issued.data.object_ref ?? null,
          file_name: file.name,
          content_type: contentType,
        },
        throwOnError: true,
      });
    },
    onMutate: () => setError(null),
    onSuccess: async () => {
      await settle();
      onClose();
    },
    onError: (caught, file) =>
      setError(
        caught instanceof SaidError
          ? caught.message
          : (fileCheckRefusal(caught, chosenFileType(file)) ??
              servedSentence(caught, w.chosenUnreadable)),
      ),
  });

  const choose = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    // The same file chosen again after a refusal still fires a change.
    event.target.value = '';
    if (file !== null) add.mutate(file);
  };

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !add.isPending && onClose()}>
      <Dialog.Content
        aria-label={w.addFileTitle}
        width={440}
        className="flex flex-col gap-16"
        testId="proposed-record-add-file-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {w.addFileTitle}
        </Dialog.Title>
        <div className="-mt-6 flex flex-col gap-6">
          <p className={`${LINE} wrap-anywhere`}>{w.addFileFor(recordTitle(record))}</p>
          {named.length > 0 ? (
            <p className={`${LINE} wrap-anywhere`}>{w.addFileNamed(named)}</p>
          ) : null}
        </div>
        <div className="flex flex-col items-start gap-8">
          <input
            ref={inputRef}
            type="file"
            accept={FILE_ACCEPT}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={choose}
            data-testid="proposed-record-add-file-input"
          />
          <Button
            appearance="saas"
            tone="secondary"
            disabled={add.isPending}
            onClick={() => inputRef.current?.click()}
            testId="proposed-record-add-file-choose"
          >
            {add.isPending ? (stage === 'check' ? w.checking : w.uploading) : w.chooseFile}
          </Button>
          <FieldNote>{w.addFileKinds}</FieldNote>
        </div>
        {error !== null ? (
          <ErrorMessage testId="proposed-record-add-file-error">{error}</ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={add.isPending}
            onClick={onClose}
            testId="proposed-record-add-file-cancel"
          >
            {w.cancel}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** The one confirmation (frames b5, c3): what goes in without a file, and what the book holds. */
function ConfirmBatchDialog({ batch, onClose }: { batch: PastRecordBatch; onClose: () => void }) {
  const operations = useBoardOperations();
  const { orgRef } = useBoardHost();
  const settle = useSettleProposals();
  const navigate = useNavigate();
  const records = stagedRecords(batch);
  const withoutFile = records.filter((record) => !hasFile(record));
  const withoutLink = goingInWithoutALink(batch);
  const inTheBook = records.filter((record) => (record.already_recorded_as ?? null) !== null);
  const closedSessions = records.filter(isClosedSession);
  const [error, setError] = useState<string | null>(null);

  const confirm = useMutation({
    mutationFn: async () => {
      // The revision alone (CBG-UX11, backend B6): Board names the batch it keeps internally.
      const { data } = await operations.officerConfirmBoardPastRecordBatch({
        path: { org_ref: orgRef ?? '', batch_id: batch.id },
        body: { revision: batch.revision },
        throwOnError: true,
      });
      return data;
    },
    onMutate: () => setError(null),
    onSuccess: async (done) => {
      toast.success(w.imported(done.imported_count));
      await settle();
      onClose();
      await navigate({ to: BOARD_PAGES.importBatches, replace: true });
    },
    // The door's own words, verbatim: changed records (6.8), or the record it couldn't take, by its
    // title (6.9). The dialog stays open.
    onError: (caught) => setError(servedSentence(caught, COULD_NOT)),
  });

  return (
    <DialogFrame
      title={w.confirmTitle(records.length)}
      lead={w.confirmLead(records.length)}
      testId="proposed-batch-confirm-dialog"
      width={520}
      onClose={() => !confirm.isPending && onClose()}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            disabled={confirm.isPending}
            onClick={onClose}
            testId="proposed-batch-confirm-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={confirm.isPending}
            onClick={() => confirm.mutate()}
            testId="proposed-batch-confirm-import"
          >
            {w.importButton(records.length)}
          </Button>
        </>
      }
    >
      {withoutFile.length > 0 ? (
        <TitledList
          lead={w.withoutFile(withoutFile.length)}
          titles={withoutFile.map((record) => ({ id: record.id, title: recordTitle(record) }))}
          testId="proposed-batch-confirm-without-file"
        />
      ) : null}
      {/* CBG-FIX160 L (frame l4): what goes in without a link, its record left out. */}
      {withoutLink.length > 0 ? (
        <TitledList
          lead={w.withoutLink(withoutLink.length)}
          titles={withoutLink.map((record) => ({ id: record.id, title: recordTitle(record) }))}
          testId="proposed-batch-confirm-without-link"
        />
      ) : null}
      {inTheBook.length > 0 ? (
        <TitledList
          lead={w.alreadyInTheBookCount(inTheBook.length)}
          titles={inTheBook.map((record) => ({ id: record.id, title: recordTitle(record) }))}
          after={w.leaveDuplicatesOut(inTheBook.length)}
          testId="proposed-batch-confirm-in-the-book"
        />
      ) : null}
      {/* After what the person may want to fix: who will read the closed sessions' records. */}
      {closedSessions.length > 0 ? (
        <TitledList
          lead={cw.closedCount(closedSessions.length)}
          titles={closedSessions.map((record) => ({ id: record.id, title: recordTitle(record) }))}
          testId="proposed-batch-confirm-closed"
        />
      ) : null}
      {error !== null ? (
        <ErrorMessage testId="proposed-batch-confirm-error">{error}</ErrorMessage>
      ) : null}
    </DialogFrame>
  );
}

/** A count's sentence, then the records it names, by title, under a leading rule (copy 6.3), and
 *  what to do about them where the dialog says (CBG-UX-COPY3's I16). */
function TitledList({
  lead,
  titles,
  after,
  testId,
}: {
  lead: string;
  titles: readonly { id: string; title: string }[];
  after?: string;
  testId: string;
}) {
  return (
    <section className="flex flex-col gap-6" data-testid={testId}>
      <p className={LINE}>{lead}</p>
      <ul className="flex flex-col gap-4 border-l-2 border-border-strong pl-12">
        {titles.map((entry) => (
          <li key={entry.id} className={`${LINE} wrap-anywhere text-text-strong`}>
            {entry.title}
          </li>
        ))}
      </ul>
      {after !== undefined ? (
        <p className={LINE} data-testid={`${testId}-way-out`}>
          {after}
        </p>
      ) : null}
    </section>
  );
}

/** Discarding the whole batch (frame b7). */
function DiscardBatchDialog({
  batch,
  records,
  attached,
  fromUploads,
  onClose,
}: {
  batch: PastRecordBatch;
  records: number;
  /** How many of the records have a file attached, and how many of those came from the uploads:
   *  Board keeps an uploaded file and sends it back there, so the body never says it's deleted. */
  attached: number;
  fromUploads: number;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const { orgRef } = useBoardHost();
  const settle = useSettleProposals();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const discard = useMutation({
    mutationFn: async () =>
      operations.officerDiscardBoardPastRecordBatch({
        path: { org_ref: orgRef ?? '', batch_id: batch.id },
        body: { revision: batch.revision },
        throwOnError: true,
      }),
    onMutate: () => setError(null),
    onSuccess: async () => {
      await settle();
      onClose();
      await navigate({ to: BOARD_PAGES.importBatches, replace: true });
    },
    onError: (caught) => setError(servedSentence(caught, COULD_NOT)),
  });
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !discard.isPending && onClose()}>
      <Dialog.Content
        aria-label={w.discardBatchTitle(records)}
        className="flex flex-col gap-16"
        testId="proposed-batch-discard-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {w.discardBatchTitle(records)}
        </Dialog.Title>
        <p className={LINE} data-testid="proposed-batch-discard-body">
          {fromUploads > 0
            ? w.discardBatchBodyUploads(fromUploads === attached)
            : w.discardBatchBody}
        </p>
        {error !== null ? (
          <ErrorMessage testId="proposed-batch-discard-error">{error}</ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={discard.isPending}
            onClick={onClose}
            testId="proposed-batch-discard-keep"
          >
            {w.keepThem}
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            disabled={discard.isPending}
            onClick={() => discard.mutate()}
            testId="proposed-batch-discard-confirm"
          >
            {w.discardAll(records)}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** Leaving one record out (frame b8), and (CBG-FIX160 L, frames l2, l3) what that does to the
 *  records linked to it. */
function LeaveOutDialog({
  record,
  batch,
  revision,
  onClose,
}: {
  record: PastRecordProposal;
  batch: PastRecordBatch;
  revision: number;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const { orgRef } = useBoardHost();
  const settle = useSettleProposals();
  const [error, setError] = useState<string | null>(null);
  // A version others replace goes out only with them, the newest first (design §8.3), so no newer
  // version stands alone where its older one was proposed; the backend refuses it alone.
  const versions = versionsReplacing(record, batch);
  const leaving = [...versions, record];
  const ids = new Set(leaving.map((one) => one.id));
  const naming = leaving.flatMap((one) =>
    recordsNamingThis(one, batch).filter((other) => !ids.has(other.id)),
  );
  const leave = useMutation({
    mutationFn: async () => {
      // Each act echoes the revision the one before it moved the batch to.
      let current = revision;
      for (const one of leaving) {
        const { data } = await operations.officerLeaveOutBoardPastRecord({
          path: { org_ref: orgRef ?? '', proposal_id: one.id },
          body: { revision: current },
          throwOnError: true,
        });
        current = typeof data.revision === 'number' ? data.revision : current + 1;
      }
    },
    onMutate: () => setError(null),
    onSuccess: async () => {
      await settle();
      onClose();
    },
    onError: async (caught) => {
      setError(servedSentence(caught, COULD_NOT));
      // A leave-out that went before a refused one moved the batch: read it again.
      if (leaving.length > 1) await settle();
    },
  });
  const title = versions.length > 0 ? w.leaveOutVersionsTitle(leaving.length) : w.leaveOutTitle;
  const attached = leaving.filter(hasFile);
  const fromUploads = attached.filter((one) => one.file.inbox_file_id).length;
  const files =
    attached.length === 0
      ? 'none'
      : fromUploads === 0
        ? 'deleted'
        : fromUploads === attached.length
          ? 'uploads'
          : 'mixed';
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !leave.isPending && onClose()}>
      <Dialog.Content
        aria-label={title}
        className="flex flex-col gap-16"
        testId="proposed-record-leave-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>
        {versions.length > 0 ? (
          <>
            <p className={`${LINE} wrap-anywhere`} data-testid="proposed-record-leave-replaced">
              {w.replacesThisVersion(recordTitle(versions[versions.length - 1]!))}
            </p>
            <p className={`${LINE} wrap-anywhere`} data-testid="proposed-record-leave-body">
              {w.versionsLeftOut(
                [...leaving].reverse().map((one) => recordTitle(one)),
                files,
              )}
            </p>
          </>
        ) : (
          <p className={`${LINE} wrap-anywhere`} data-testid="proposed-record-leave-body">
            {record.file.inbox_file_id
              ? w.leaveOutBodyUpload(recordTitle(record))
              : w.leaveOutBody(recordTitle(record), hasFile(record))}
          </p>
        )}
        {naming.map((other) => (
          <p
            key={`${other.kind}-${other.id}`}
            className={`${LINE} wrap-anywhere`}
            data-testid={`proposed-record-leave-named-${other.id}`}
          >
            {other.kind === 'meeting'
              ? w.namesThisAsMeeting(other.title)
              : w.namesThisAsAdopting(other.title)}
          </p>
        ))}
        {error !== null ? (
          <ErrorMessage testId="proposed-record-leave-error">{error}</ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={leave.isPending}
            onClick={onClose}
            testId="proposed-record-leave-keep"
          >
            {versions.length > 0 ? w.keepThem : w.keepIt}
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            disabled={leave.isPending}
            onClick={() => leave.mutate()}
            testId="proposed-record-leave-confirm"
          >
            {versions.length > 0 ? w.leaveOutVersions(leaving.length) : w.leaveItOut}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * Marking one record as a closed session's own, or not (CBG-AI5-CLOSED): who will read it is said
 * as the person chooses, each way. The batch's revision is echoed, as every act here echoes it.
 */
function MarkClosedDialog({
  record,
  revision,
  onClose,
}: {
  record: PastRecordProposal;
  revision: number;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const { orgRef } = useBoardHost();
  const settle = useSettleProposals();
  const [error, setError] = useState<string | null>(null);
  const closed = isClosedSession(record);
  const title = closed ? cw.markNotClosedTitle : cw.markClosedTitle;
  const mark = useMutation({
    mutationFn: async () =>
      operations.officerMarkBoardPastRecordConfidentiality({
        path: { org_ref: orgRef ?? '', proposal_id: record.id },
        body: { revision, confidentiality: closed ? 'standard' : 'executive_session' },
        throwOnError: true,
      }),
    onMutate: () => setError(null),
    onSuccess: async () => {
      await settle();
      onClose();
    },
    onError: (caught) => setError(servedSentence(caught, COULD_NOT)),
  });
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !mark.isPending && onClose()}>
      <Dialog.Content
        aria-label={title}
        className="flex flex-col gap-16"
        testId="proposed-record-closed-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>
        <p className={`${LINE} wrap-anywhere`} data-testid="proposed-record-closed-body">
          {closed
            ? cw.markNotClosedBody(recordTitle(record))
            : cw.markClosedBody(recordTitle(record))}
        </p>
        {error !== null ? (
          <ErrorMessage testId="proposed-record-closed-error">{error}</ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={mark.isPending}
            onClick={onClose}
            testId="proposed-record-closed-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={mark.isPending}
            onClick={() => mark.mutate()}
            testId="proposed-record-closed-confirm"
          >
            {cw.markIt}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
